import { SCORING } from '../config/tuning';
import type { ObjectiveTracker } from './Objectives';
import type { Mission } from './schema';

/**
 * Score and rank (SPEC §3.12).
 *
 * The components are named in the spec: objective completion, aim point accuracy, missiles evaded
 * scaled by how close and how correctly, lowest average ingress altitude, endurance remaining,
 * collateral avoidance, and time.
 *
 * Two decisions worth stating, because they are what stop the score being noise:
 *
 * Each component is normalised to 0–1 *before* the mission's weights apply, so a mission can say
 * "this one is about altitude discipline" by weighting one term up, without any component being
 * able to dominate by accident of its units. Metres and seconds are not comparable numbers.
 *
 * Evasion is scored on *how close* the missile got. A missile defeated at 400 m was defeated by
 * flying somewhere else; one defeated at 30 m was defeated by a decision. Rewarding the latter is
 * the whole reason the recorder exists (SPEC §3.6.5).
 */

export const Rank = {
  Unrated: 'UNRATED',
  Qualified: 'QUALIFIED',
  Proficient: 'PROFICIENT',
  Expert: 'EXPERT',
} as const;
export type RankId = (typeof Rank)[keyof typeof Rank];

/** Everything the run produced, gathered by the recorder. */
export interface RunSummary {
  readonly elapsedS: number;
  /** 0–1 at mission end. */
  readonly integrity: number;
  /** Fraction of fuel remaining, 0–1. */
  readonly fuelFraction: number;
  /** Metres from the aim point on the terminal strike, or null if there was none. */
  readonly aimPointDistanceM: number | null;
  /** Warhead effect radius the strike was graded against, metres. */
  readonly aimPointReferenceM: number;
  /** Mean AGL altitude over the ingress, metres. */
  readonly meanIngressAltitudeM: number;
  /** Closest approach of every missile that failed to destroy the aircraft, metres. */
  readonly evadedMissDistancesM: readonly number[];
  /** Missiles that hit. */
  readonly missileHits: number;
  /** Things struck that the brief said to leave alone. */
  readonly collateralStrikes: number;
}

export interface ScoreBreakdown {
  readonly component: string;
  /** 0–1 before weighting. */
  readonly normalised: number;
  readonly weight: number;
  readonly points: number;
}

export interface MissionScore {
  readonly total: number;
  readonly rank: RankId;
  readonly breakdown: readonly ScoreBreakdown[];
  /** Why expert was not awarded, for the debrief. Empty when it was. */
  readonly expertShortfalls: readonly string[];
}

/** Fraction of objective weight completed, 0–1. */
export function objectiveCompletion(objectives: ObjectiveTracker): number {
  let earned = 0;
  let available = 0;
  for (let i = 0; i < objectives.statuses.length; i += 1) {
    const status = objectives.statuses[i];
    available += status.weight;
    if (status.state === 'complete') earned += status.weight;
  }
  return available <= 0 ? 0 : earned / available;
}

/**
 * Accuracy term, 0–1, from distance to the aim point.
 *
 * Measured against the warhead's own effect radius rather than a fixed distance, so a penetrator's
 * tighter tolerance is reflected in the score the same way it is in the damage model.
 */
export function accuracyScore(distanceM: number | null, referenceM: number): number {
  if (distanceM === null) return 0;
  if (referenceM <= 0) return 0;
  return clamp01(1 - distanceM / referenceM);
}

/**
 * Evasion term, 0–1, over every missile survived.
 *
 * A miss at or inside `evasionIdealM` scores full: that is a break turn timed properly. Beyond
 * `evasionLooseM` it scores nothing — the missile was never going to arrive. Between them it
 * ramps, because the interesting part of the skill lives in that band.
 */
export function evasionScore(missDistancesM: readonly number[]): number {
  if (missDistancesM.length === 0) return 1;
  let total = 0;
  for (let i = 0; i < missDistancesM.length; i += 1) {
    const distanceM = missDistancesM[i];
    if (distanceM <= SCORING.evasionIdealM) {
      total += 1;
    } else if (distanceM >= SCORING.evasionLooseM) {
      total += SCORING.evasionLooseCredit;
    } else {
      const span = SCORING.evasionLooseM - SCORING.evasionIdealM;
      const t = (distanceM - SCORING.evasionIdealM) / span;
      total += 1 - (1 - SCORING.evasionLooseCredit) * t;
    }
  }
  return clamp01(total / missDistancesM.length);
}

/** Ingress altitude term, 0–1. Lower is better, floored so the ground is not the optimum. */
export function altitudeScore(meanAltitudeM: number): number {
  if (meanAltitudeM <= SCORING.idealIngressAltM) return 1;
  if (meanAltitudeM >= SCORING.poorIngressAltM) return 0;
  const span = SCORING.poorIngressAltM - SCORING.idealIngressAltM;
  return clamp01(1 - (meanAltitudeM - SCORING.idealIngressAltM) / span);
}

/** Time term, 0–1, against the mission's par. Beating par saturates rather than compounding. */
export function timeScore(elapsedS: number, parTimeS: number): number {
  if (parTimeS <= 0) return 0;
  if (elapsedS <= parTimeS) return 1;
  const overrun = (elapsedS - parTimeS) / parTimeS;
  return clamp01(1 - overrun / SCORING.timeOverrunTolerance);
}

const COMPONENTS = [
  'objectives',
  'accuracy',
  'evasion',
  'altitude',
  'endurance',
  'collateral',
  'time',
] as const;

/**
 * Score a completed run.
 *
 * Weights come from the mission so a tutorial can score almost entirely on objectives while a
 * later mission rewards the discipline it exists to teach. An unweighted component contributes
 * nothing rather than defaulting to one — silence in the data should mean silence in the score.
 */
export function scoreMission(
  mission: Mission,
  objectives: ObjectiveTracker,
  run: RunSummary,
): MissionScore {
  const normalised: Record<string, number> = {
    objectives: objectiveCompletion(objectives),
    accuracy: accuracyScore(run.aimPointDistanceM, run.aimPointReferenceM),
    evasion: evasionScore(run.evadedMissDistancesM),
    altitude: altitudeScore(run.meanIngressAltitudeM),
    endurance: clamp01(run.fuelFraction),
    collateral: run.collateralStrikes > 0 ? 0 : 1,
    time: timeScore(run.elapsedS, mission.scoring.parTimeS),
  };

  const breakdown: ScoreBreakdown[] = [];
  let total = 0;
  for (let i = 0; i < COMPONENTS.length; i += 1) {
    const component = COMPONENTS[i];
    const weight = mission.scoring.weights[component] ?? 0;
    if (weight <= 0) continue;
    const points = normalised[component] * weight;
    total += points;
    breakdown.push({ component, normalised: normalised[component], weight, points });
  }

  const expertShortfalls = expertGap(mission, objectives, run);
  return {
    total: Math.round(total),
    rank: rankFor(total, normalised.objectives, objectives, expertShortfalls),
    breakdown,
    expertShortfalls,
  };
}

/**
 * What stood between this run and expert (SPEC §3.12: no damage and all secondary objectives).
 *
 * Returned as reasons rather than a boolean because the debrief has to *explain* the rank. "You
 * were not awarded expert" is a scold; "expert requires an undamaged airframe — you finished at
 * 86%" is a lesson.
 */
function expertGap(
  mission: Mission,
  objectives: ObjectiveTracker,
  run: RunSummary,
): string[] {
  const shortfalls: string[] = [];
  if (!objectives.allPrimaryComplete) shortfalls.push('a primary objective was not completed');
  if (run.integrity < 1) {
    shortfalls.push(`expert requires an undamaged airframe — you finished at ${Math.round(run.integrity * 100)}%`);
  }
  for (let i = 0; i < objectives.statuses.length; i += 1) {
    const status = objectives.statuses[i];
    if (!status.primary && status.state !== 'complete') {
      shortfalls.push(`secondary objective incomplete: ${status.description}`);
    }
  }
  for (let i = 0; i < mission.scoring.expertRequires.length; i += 1) {
    const id = mission.scoring.expertRequires[i];
    const status = objectives.find(id);
    if (status === null) continue;
    if (status.state !== 'complete') {
      shortfalls.push(`this mission requires "${status.description}" for expert`);
    }
  }
  return shortfalls;
}

function rankFor(
  total: number,
  objectiveFraction: number,
  objectives: ObjectiveTracker,
  expertShortfalls: readonly string[],
): RankId {
  if (!objectives.allPrimaryComplete) return Rank.Unrated;
  if (expertShortfalls.length === 0) return Rank.Expert;
  if (total >= SCORING.proficientScore && objectiveFraction >= SCORING.proficientObjectives) {
    return Rank.Proficient;
  }
  return Rank.Qualified;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
